// CreateUserStoryForm.jsx
import React, { useState } from "react";
import {
    TextField,
    Grid,
    Typography,
    Box,
    Button,
    Autocomplete,
    Snackbar,
    Alert,
    Chip,
    Card,
    CardContent,
    IconButton,
    alpha, MenuItem, FormControl, InputLabel, Select
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Close, Description } from "@mui/icons-material";

export default function CreateUserStoryForm({ onComplete, epicName = "Share-Ur-Truck" }) { // Added epicName prop with default
    // ---------------------------------------------------------
    // MOCK OPTIONS (Replace with API response later)
    // ---------------------------------------------------------
    const productOwnerOptions = ["Venku Naidu Velle", "Arjun Dev", "Harsha Kumar"];
    const teamLeadOptions = ["Viyyapu Naveen Kumar", "Sumanth", "David"];
    const priorityOptions = ["High", "Medium", "Low"];

    // ---------------------------------------------------------
    // FORM STATE
    // ---------------------------------------------------------
    const [form, setForm] = useState({
        title: "",
        productOwner: "",
        teamLead: "",
        startDate: null,
        dueDate: null,
        priority: "Medium",
        description: "As a [user type], I want [some goal], so that [reason].",
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // ---------------------------------------------------------
    // HANDLERS
    // ---------------------------------------------------------
    const handleFieldChange = (field, value) => {
        setForm({ ...form, [field]: value });
    };

    const handleCreate = () => {
        // Validation
        if (!form.title || !form.productOwner) {
            setSnackbar({
                open: true,
                message: "Please fill all required fields",
                severity: "error",
            });
            return;
        }

        setSnackbar({
            open: true,
            message: "User Story Created Successfully!",
            severity: "success",
        });

        if (onComplete) {
            onComplete({
                id: Date.now(),
                title: form.title,
                epic: epicName, // Use epicName from prop
                priority: form.priority,
                productOwner: form.productOwner,
                teamLead: form.teamLead,
                startDate: form.startDate,
                dueDate: form.dueDate,
                description: form.description
            });
        }
    };

    const handleCancel = () => {
        if (onComplete) {
            onComplete(null);
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case "High": return { bg: alpha('#ef4444', 0.1), color: '#dc2626' };
            case "Medium": return { bg: alpha('#f59e0b', 0.1), color: '#d97706' };
            case "Low": return { bg: alpha('#10b981', 0.1), color: '#059669' };
            default: return { bg: alpha('#6b7280', 0.1), color: '#4b5563' };
        }
    };

    const priorityColor = getPriorityColor(form.priority);

    return (
        <Card
            sx={{
                borderRadius: 2,
                boxShadow: '0 1px 20px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: alpha('#8b5cf6', 0.15),
                overflow: 'hidden'
            }}
        >
            {/* Header with Epic Name */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${alpha('#8b5cf6', 0.1)} 0%, ${alpha('#7c3aed', 0.1)} 100%)`,
                    borderBottom: '1px solid',
                    borderBottomColor: alpha('#8b5cf6', 0.1),
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        p: 0.5,
                        borderRadius: 1.5,
                        backgroundColor: alpha('#8b5cf6', 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Description sx={{ color: '#8b5cf6', fontSize: 20 }} />
                    </Box>
                    <Box >
                        <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 600 }}>
                            Create User Story
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                            Epic: {epicName}
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={handleCancel}
                    size="small"
                    sx={{
                        color: '#64748b',
                        '&:hover': {
                            backgroundColor: alpha('#64748b', 0.1)
                        }
                    }}
                >
                    <Close />
                </IconButton>
            </Box>

            {/* Form Content */}
            <CardContent sx={{ p: 3 }}>
                <Grid container spacing={1.5}>
                    {/* User Story Title - Full Width */}
                    <Grid item xs={12}>
                        <TextField
                            label="User Story Title"
                            required
                            fullWidth
                            value={form.title}
                            onChange={(e) => handleFieldChange("title", e.target.value)}
                            size="medium"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5
                                }
                            }}
                        />
                    </Grid>

                    {/* Manager (Product Owner) */}
                    {/* <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={productOwnerOptions}
                            value={form.productOwner}
                            onChange={(e, v) => handleFieldChange("productOwner", v)}
                            size="medium"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Manager"
                                    required
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid> */}

                    {/* Product Owner */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={teamLeadOptions}
                            value={form.teamLead}
                            onChange={(e, v) => handleFieldChange("teamLead", v)}
                            size="medium"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Product Owner"
                                    fullWidth
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    {/* Priority in one grid of 6 */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="medium">
                            <InputLabel id="priority-select-label">Priority</InputLabel>
                            <Select
                                labelId="priority-select-label"
                                label="Priority"
                                value={form.priority}
                                onChange={(e) => handleFieldChange("priority", e.target.value)}
                                sx={{
                                    borderRadius: 1.5,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5
                                    }
                                }}
                                renderValue={(selected) => {
                                    const color = getPriorityColor(selected);
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: color.color
                                                }}
                                            />
                                            <Typography variant="body2">{selected}</Typography>
                                        </Box>
                                    );
                                }}
                            >
                                {priorityOptions.map((option) => {
                                    const optionColor = getPriorityColor(option);
                                    return (
                                        <MenuItem
                                            key={option}
                                            value={option}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: optionColor.color
                                                }}
                                            />
                                            {option}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    {/* Start Date and Due Date in one row */}
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Start Date"
                                    value={form.startDate}
                                    onChange={(date) => handleFieldChange("startDate", date)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1.5
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>

                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Due Date"
                                    value={form.dueDate}
                                    onChange={(date) => handleFieldChange("dueDate", date)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 1.5
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Grid>



                    {/* Description - Full Width */}
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            multiline
                            rows={4}
                            fullWidth
                            value={form.description}
                            onChange={(e) => handleFieldChange("description", e.target.value)}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </CardContent>

            {/* Action Buttons */}
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderTopColor: alpha('#e2e8f0', 1),
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.5
                }}
            >
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        borderColor: alpha('#94a3b8', 0.5),
                        color: '#64748b',
                        px: 2,
                        py: 0.75,
                        fontSize: '0.875rem',
                        '&:hover': {
                            borderColor: '#64748b',
                            backgroundColor: 'transparent'
                        }
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={handleCreate}
                    variant="contained"
                    startIcon={<Description sx={{ fontSize: 18 }} />}
                    sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        backgroundColor: 'primary.main',
                        px: 3,
                        py: 0.75,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: 'primary.dark'
                        }
                    }}
                >
                    Create User Story
                </Button>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    sx={{
                        width: '100%',
                        borderRadius: 1.5
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Card>
    );
}